import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
  Avatar,
  Chip,
  Divider,
  CircularProgress,
  Button,
  Stack,
  Tooltip,
  alpha,
  useTheme,
  Grid,
} from "@mui/material";
import {
  Close as CloseIcon,
  Phone as PhoneIcon,
  WhatsApp as WhatsAppIcon,
  CalendarToday as CalendarIcon,
  LocationOn as LocationIcon,
  AssignmentTurnedIn as DiscussionIcon,
  OpenInNew as OpenInNewIcon,
  Timeline as TimelineIcon,
  ForumOutlined,
  PersonOutlined,
} from "@mui/icons-material";
import moment from "moment";
import { capitalizeWords } from "functions";
import useAxiosPrivate from "app/hooks/useAxiosPrivate";

function getUrgency(dateStr) {
  if (!dateStr) return "future";
  const parts = dateStr.split("/");
  if (parts.length < 3) return "future";
  const day = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1;
  const year = parseInt(parts[2].split(" ")[0], 10);
  if (isNaN(day) || isNaN(month) || isNaN(year)) return "future";
  const date = new Date(year, month, day);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (date < today) return "overdue";
  if (date.toDateString() === today.toDateString()) return "today";
  return "future";
}

const urgencyLabel = { overdue: "Overdue", today: "Today", future: "Upcoming" };
const urgencyColor = { overdue: "error", today: "warning", future: "success" };

export default function FollowupDetailsDialog({ open, onClose, followup }) {
  const theme = useTheme();
  const axiosPrivate = useAxiosPrivate();

  const [loading, setLoading] = useState(false);
  const [discussionDetails, setDiscussionDetails] = useState(null);
  const [historyDiscussions, setHistoryDiscussions] = useState([]);
  const [contactData, setContactData] = useState(null);

  const urgency = getUrgency(followup?.NextAppointmentDate);

  useEffect(() => {
    let isMounted = true;
    if (open && followup) {
      setLoading(true);
      setDiscussionDetails(null);
      setHistoryDiscussions([]);
      setContactData(null);

      // 1. Fetch discussion details by UniqueID if available
      const discPromise = followup.UniqueID
        ? axiosPrivate.post("GetDiscussionByID.php", { UID: followup.UniqueID })
        : Promise.resolve(null);

      // 2. Fetch contact / reference details by ProspectID
      const contactPromise = followup.ProspectID
        ? axiosPrivate.post("GetReferenceByIDNew.php", { UID: followup.ProspectID })
        : Promise.resolve(null);

      Promise.all([discPromise, contactPromise])
        .then(([discRes, contactRes]) => {
          if (!isMounted) return;
          if (discRes?.data?.status === 1) {
            setDiscussionDetails(discRes.data.Data?.[0] || null);
            setHistoryDiscussions(discRes.data.Discussions || []);
          }
          if (contactRes?.data?.status === 1 && contactRes.data.Data?.[0]) {
            setContactData(contactRes.data.Data[0]);
          }
        })
        .catch((err) => {
          console.error("Error loading followup details:", err);
        })
        .finally(() => {
          if (isMounted) setLoading(false);
        });
    }

    return () => {
      isMounted = false;
    };
  }, [open, followup, axiosPrivate]);

  if (!followup) return null;

  const prospectLink =
    followup.Type === "Agent"
      ? `/CLIAProspect/EditCLIAProspect/${followup.ProspectID}`
      : `/Prospects/ViewProspect/${followup.ProspectID}`;

  const conceptLink = `/Prospects/ConceptDiscussion/${followup.ProspectID}`;

  const mobileNumber = contactData?.MobileNo || contactData?.Mobile || followup.Mobile;
  const whatsappNumber = contactData?.WhatsAppNo || mobileNumber;

  const displayPlace = discussionDetails?.Place || followup.Place;
  const displayDateTime = discussionDetails?.DateTime || followup.DateTime;
  const displaySubject = discussionDetails?.Subject || followup.Subject;
  const displayResult = discussionDetails?.Result || followup.Result || followup.LatestResult;
  const displayFamilyMember = discussionDetails?.FamilyMemberName || followup.FamilyMemberName;
  const displayLeadStatus = followup.LeadStatus;
  const displayProfession = contactData?.Profession || followup.Profession || followup.WorkType || followup.Type;
  const displayLocation = contactData?.Village || contactData?.City || followup.Village;
  const hasDiscussionNotes = Boolean(displayPlace || displayDateTime || displaySubject || displayResult || displayFamilyMember || displayLeadStatus);

  const handleWhatsApp = () => {
    if (!whatsappNumber) return;
    const name = capitalizeWords((followup.Name || "").split(" ")[0]);
    const msg = encodeURIComponent(
      `Hello ${name}, following up regarding our upcoming discussion scheduled for ${followup.NextAppointmentDate || "soon"}.`
    );
    window.open(`https://wa.me/91${whatsappNumber}?text=${msg}`, "_blank");
  };

  const handleCall = () => {
    if (!mobileNumber) return;
    window.open(`tel:${mobileNumber}`);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3.5,
          overflow: "hidden",
          background:
            theme.palette.mode === "dark"
              ? alpha(theme.palette.background.paper, 0.95)
              : "#ffffff",
          backdropFilter: "blur(20px)",
          boxShadow: theme.shadows[10],
        },
      }}
    >
      {/* ── Dialog Header ── */}
      <DialogTitle
        sx={{
          m: 0,
          p: 2.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          bgcolor: alpha(theme.palette.primary.main, 0.05),
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: "11px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              color: theme.palette.primary.main,
            }}
          >
            <DiscussionIcon sx={{ fontSize: 22 }} />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.2 }}>
              Follow-up & Discussion Details
            </Typography>
            <Typography variant="caption" color="text.secondary">
              ID: {followup.ID || followup.ProspectID}
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={0.5} alignItems="center">
          <Chip
            label={urgencyLabel[urgency]}
            color={urgencyColor[urgency]}
            size="small"
            sx={{ fontWeight: 700, fontSize: "0.7rem", height: 22 }}
          />
          <Tooltip title="View Complete Prospect" arrow>
            <IconButton
              component="a"
              href={prospectLink}
              target="_blank"
              size="small"
              color="primary"
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <IconButton onClick={onClose} size="small" sx={{ color: "text.secondary" }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>

      {/* ── Dialog Content ── */}
      <DialogContent sx={{ p: 3 }}>
        {/* Prospect Header Card */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
            p: 2,
            mb: 3,
            borderRadius: 2.5,
            bgcolor: alpha(theme.palette.action.hover, 0.35),
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Avatar
              src={
                contactData?.Picture
                  ? `https://backend.dahiyainsurance.com/Upload/profile/${contactData.Picture}`
                  : undefined
              }
              sx={{
                width: 54,
                height: 54,
                fontWeight: 700,
                fontSize: "1.2rem",
                bgcolor: alpha(theme.palette.primary.main, 0.15),
                color: theme.palette.primary.main,
              }}
            >
              {followup.Name?.[0]?.toUpperCase() || <PersonOutlined />}
            </Avatar>
            <Box>
              <Typography variant="subtitle1" fontWeight={800} sx={{ textTransform: "capitalize" }}>
                {capitalizeWords(followup.Name)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {[displayProfession, displayLocation].filter(Boolean).join(" • ") || "Prospect"}
              </Typography>
            </Box>
          </Box>

          {/* Quick Contacts */}
          {mobileNumber && (
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleCall}
                startIcon={<PhoneIcon sx={{ fontSize: 16 }} />}
                sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
              >
                {mobileNumber}
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={handleWhatsApp}
                startIcon={<WhatsAppIcon sx={{ fontSize: 16 }} />}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 700,
                  bgcolor: "#25D366",
                  color: "#fff",
                  "&:hover": { bgcolor: "#1EBE5D" },
                }}
              >
                WhatsApp
              </Button>
            </Stack>
          )}
        </Box>

        {/* Next Appointment & Discussed Overview */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6}>
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                height: "100%",
                bgcolor: alpha(theme.palette.warning.main, 0.05),
                border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.8 }}>
                <CalendarIcon sx={{ fontSize: 18, color: theme.palette.warning.main }} />
                <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: "uppercase" }}>
                  Next Scheduled Follow-up
                </Typography>
              </Stack>
              <Typography variant="h6" fontWeight={800} color="text.primary">
                {followup.NextAppointmentDate || discussionDetails?.NextAppointment || "No date set"}
              </Typography>
              {(discussionDetails?.NextPlace || followup.NextPlace) && (
                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
                  <LocationIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                  <Typography variant="caption" color="text.secondary">
                    Location: {discussionDetails?.NextPlace || followup.NextPlace}
                  </Typography>
                </Stack>
              )}
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                height: "100%",
                bgcolor: alpha(theme.palette.info.main, 0.05),
                border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.8 }}>
                <ForumOutlined sx={{ fontSize: 18, color: theme.palette.info.main }} />
                <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: "uppercase" }}>
                  Topic / Concept Discussed
                </Typography>
              </Stack>
              <Typography variant="subtitle1" fontWeight={700} color="text.primary">
                {followup.Discussed || discussionDetails?.Discussed || "General Discussion"}
              </Typography>
              {(discussionDetails?.Subject || followup.Subject) && (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                  Subject: {discussionDetails?.Subject || followup.Subject}
                </Typography>
              )}
            </Box>
          </Grid>
        </Grid>

        {/* Discussion Details & Notes */}
        {loading ? (
          <Box sx={{ py: 3, display: "flex", justifyContent: "center" }}>
            <CircularProgress size={32} />
          </Box>
        ) : (
          <>
            {hasDiscussionNotes && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, display: "flex", alignItems: "center", gap: 0.75 }}>
                  <DiscussionIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} /> Current Discussion Notes
                </Typography>
                <Grid container spacing={2}>
                  {displayDateTime && (
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="caption" color="text.secondary" display="block">Meeting Date & Time</Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {moment(displayDateTime).isValid()
                          ? moment(displayDateTime).format("DD MMM YYYY, hh:mm A")
                          : displayDateTime}
                      </Typography>
                    </Grid>
                  )}
                  {displayPlace && (
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="caption" color="text.secondary" display="block">Meeting Place</Typography>
                      <Typography variant="body2" fontWeight={600}>{displayPlace}</Typography>
                    </Grid>
                  )}
                  {displayResult && (
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="caption" color="text.secondary" display="block">Latest Result</Typography>
                      <Chip label={displayResult} size="small" sx={{ height: 20, fontSize: "0.68rem" }} />
                    </Grid>
                  )}
                  {displayLeadStatus && (
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="caption" color="text.secondary" display="block">Lead Status</Typography>
                      <Chip label={displayLeadStatus} size="small" variant="outlined" color="primary" sx={{ height: 20, fontSize: "0.68rem" }} />
                    </Grid>
                  )}
                  {displayFamilyMember && (
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="caption" color="text.secondary" display="block">Family Member</Typography>
                      <Typography variant="body2" fontWeight={600}>{displayFamilyMember}</Typography>
                    </Grid>
                  )}
                </Grid>
              </Box>
            )}

            {/* History / Previous Followups Timeline */}
            {historyDiscussions.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, display: "flex", alignItems: "center", gap: 0.75 }}>
                  <TimelineIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} /> Discussion History ({historyDiscussions.length})
                </Typography>
                <Stack spacing={1.5}>
                  {historyDiscussions.map((item, idx) => (
                    <Box
                      key={item.UniqueID || item.ID || idx}
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.action.hover, 0.25),
                        borderLeft: `3px solid ${theme.palette.primary.main}`,
                      }}
                    >
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                        <Typography variant="caption" fontWeight={700} color="text.secondary">
                          {item.DateTime ? moment(item.DateTime).format("DD MMM YYYY, hh:mm A") : `Entry #${idx + 1}`}
                        </Typography>
                        {item.Status && (
                          <Chip label={item.Status} size="small" sx={{ height: 18, fontSize: "0.62rem" }} />
                        )}
                      </Box>
                      <Typography variant="body2" fontWeight={600}>
                        {item.Discussed || item.Subject || "Followup note"}
                      </Typography>
                      {item.NextAppointment && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                          Next: {moment(item.NextAppointment).format("DD MMM YYYY, hh:mm A")} {item.NextPlace ? `at ${item.NextPlace}` : ""}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}
          </>
        )}
      </DialogContent>

      {/* ── Dialog Actions ── */}
      <DialogActions sx={{ p: 2.5, bgcolor: alpha(theme.palette.action.hover, 0.2), display: "flex", justifyContent: "space-between" }}>
        <Button onClick={onClose} color="inherit" sx={{ textTransform: "none" }}>
          Close
        </Button>

        <Stack direction="row" spacing={1.5}>
          <Button
            component="a"
            href={conceptLink}
            target="_blank"
            variant="outlined"
            size="small"
            startIcon={<ForumOutlined />}
            sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
          >
            All Discussions
          </Button>

          <Button
            component="a"
            href={prospectLink}
            target="_blank"
            variant="contained"
            size="small"
            endIcon={<OpenInNewIcon sx={{ fontSize: 15 }} />}
            sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700 }}
          >
            View Complete Prospect
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
